import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import CommentListItemHeader from './CommentList/CommentListItemHeader'
import { Box, Button, Divider, HStack, Text, useToast } from '@chakra-ui/react'
import CustomMentionInput from '../../ui/CustomMentionInput'
import rootStore from '../../../store/RootStore'
import { observer } from 'mobx-react'

const CommentForm = observer(props => {
  const { commentStore, authStore } = rootStore
  const toast = useToast()
  const commentTextArea = useRef(null)
  const [text, setText] = useState('')

  useEffect(() => {
    setText(commentStore.tempComment.text || '')
    if (commentTextArea.current) {
      commentTextArea.current.focus()
    }
    if (!commentStore.isEditing) {
      setText('')
    }
  }, [
    commentStore.tempComment,
    commentStore.isFormVisible,
    commentStore.isEditing,
  ])

  const handleCancelComment = useCallback(() => {
    commentStore.setIsFormVisible(false)
  }, [commentStore])

  const handleSaveComment = useCallback(() => {
    const toastConfig = {
      status: 'success',
    }

    if (commentStore.isEditing) {
      commentStore.updateComment(text)
      toastConfig.title = 'Comment has been updated.'
    } else {
      commentStore.createComment(commentTextArea.current.value, authStore.user)
      toastConfig.title = 'Comment added.'
    }

    if (commentStore.isEditing) {
      commentStore.updateComment(text)
      toastConfig.title = 'Comment has been updated.'
    } else {
      commentStore.createComment(commentTextArea.current.value, authStore.user)
      toastConfig.title = 'Comment added.'
    }

    commentStore.setIsFormVisible(false)
    return toast(toastConfig)
  }, [authStore.user, commentStore, toast, text])

  const handleMentionInputChange = useCallback(e => {
    setText(e.target.value)
  }, [])

  const answeringFor = useMemo(
    () =>
      commentStore.chartComments.comments?.find(
        comment => comment.id === commentStore.tempComment.parentId,
      ),
    [commentStore.chartComments.comments, commentStore.tempComment.parentId],
  )

  if (!commentStore.isFormVisible) return null

  return (
    <Box {...props}>
      {(commentStore.tempComment.id || commentStore.tempComment.parentId) && (
        <Box mt={'12px'}>
          <Text
            textStyle="comments"
            fontWeight="bold">
            {commentStore.isEditing ? 'Editing:' : 'Reply to'}
          </Text>

          <CommentListItemHeader
            user={
              commentStore.isEditing
                ? commentStore.tempComment.user
                : answeringFor?.user
            }
            dateUpdated={
              commentStore.isEditing
                ? commentStore.tempComment.updated
                : answeringFor?.updated
            }
            hideMoreMenu
            pl="0"
          />
        </Box>
      )}

      <Box position="relative">
        {!(
          commentStore.tempComment.id || commentStore.tempComment.parentId
        ) && (
          <Text
            mt={'12px'}
            mb={'12px'}
            textStyle="comments"
            fontWeight="bold">
            {commentStore.tempComment.id ? 'Editing' : 'New'} Comment:
          </Text>
        )}
        <CustomMentionInput
          onChange={handleMentionInputChange}
          text={text}
          ref={commentTextArea}
          singleLine={false}
          allowSuggestionsAboveCursor
        />
        <HStack
          mt="16px"
          justifyContent="flex-end">
          <Button
            size="sm"
            onClick={handleCancelComment}
            colorScheme="blackAlpha"
            data-testid="cancel-comment-button">
            Cancel
          </Button>
          <Button
            size="sm"
            colorScheme="orange"
            onClick={handleSaveComment}
            width="auto"
            height="auto"
            p="8px 24px"
            data-testid="save-comment-button">
            Save
          </Button>
        </HStack>
      </Box>
      <Divider
        width="calc(100% + 48px)"
        ml="-24px"
        mt="16px"
        mb="-14px"
      />
    </Box>
  )
})

CommentForm.propTypes = {
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  forwardRef: PropTypes.any,
  isResponse: PropTypes.bool,
  tempComment: PropTypes.any,
}
CommentForm.displayName = 'CommentForm'
export default CommentForm
